import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import PhoneCallbackOutlinedIcon from '@mui/icons-material/PhoneCallbackOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { createElement, type CSSProperties } from 'react'
import type { CRMTextoTipo } from '../../../model/kartera-api-model'

export {
    daysFromToday,
    eventParts,
    formatActivityDate,
    formatMonthYear,
    formatSlashDate,
    formatSpokenDate,
    formatWithAge,
    parseDate,
    toDate,
} from '../../../utils/dates'

type MarkIcon = typeof DescriptionOutlinedIcon

type NameParts = {
    nombres?: string | null
    apPat?: string | null
    apMat?: string | null
}

type RamoRef = string | { ramo?: string | null } | null | undefined

type RamoVisual = {
    Icon: MarkIcon
    tint: string
    ink: string
}

const RAMOS: Array<{ test: RegExp; Icon: MarkIcon; tint: string; ink: string }> = [
    { test: /auto|flotilla|vehic/, Icon: DirectionsCarOutlinedIcon, tint: 'bg-tone-blue text-tone-blue-ink', ink: 'text-tone-blue-ink' },
    { test: /vida/, Icon: PersonOutlinedIcon, tint: 'bg-tone-purple text-tone-purple-ink', ink: 'text-tone-purple-ink' },
    { test: /salud|gmm|medic|médic/, Icon: FavoriteBorderOutlinedIcon, tint: 'bg-tone-pink text-tone-pink-ink', ink: 'text-tone-pink-ink' },
    { test: /divers|dañ|dan|hogar|casa|incendio/, Icon: HomeOutlinedIcon, tint: 'bg-tone-orange text-tone-orange-ink', ink: 'text-tone-orange-ink' },
]

const FALLBACK_RAMO: RamoVisual = { Icon: DescriptionOutlinedIcon, tint: 'bg-k-line text-k-ink-2', ink: 'text-k-ink-2' }

export const personName = (cliente: NameParts) =>
    [cliente.nombres, cliente.apPat, cliente.apMat].filter((part): part is string => Boolean(part)).join(' ')

export const isMoral = (fiscal?: string | null) => String(fiscal || '').toLowerCase() === 'moral'

export const fiscalLabel = (fiscal?: string | null) => {
    const value = String(fiscal || '').toLowerCase()
    if (value === 'moral') return 'Moral'
    if (value === 'fisica') return 'Física'
    return fiscal || '—'
}

export type BitacoraFilter = 'todo' | 'nota' | 'llamada' | 'correo' | 'reunion' | 'otro'

export interface NoteMeta {
    label: string
    filter: BitacoraFilter
    Icon: MarkIcon
    tone: string
    placeholder?: string
}

export interface NoteType extends NoteMeta {
    id: CRMTextoTipo
    placeholder: string
}

export const NOTE_TYPES: NoteType[] = [
    { id: 'nota general', label: 'Nota', filter: 'nota', Icon: StickyNote2OutlinedIcon, tone: 'bg-tone-purple text-tone-purple-ink', placeholder: 'Escribe una nota interna…' },
    { id: 'llamada saliente', label: 'Llamada', filter: 'llamada', Icon: PhoneOutlinedIcon, tone: 'bg-tone-teal text-tone-teal-ink', placeholder: '¿De qué hablaron en la llamada?' },
    { id: 'correo', label: 'Correo', filter: 'correo', Icon: EmailOutlinedIcon, tone: 'bg-tone-blue text-tone-blue-ink', placeholder: 'Resumen del correo enviado o recibido…' },
    { id: 'platica con humano', label: 'Reunión', filter: 'reunion', Icon: EventOutlinedIcon, tone: 'bg-tone-orange text-tone-orange-ink', placeholder: 'Acuerdos de la reunión…' },
]

export const BITACORA_FILTERS: Array<{ id: BitacoraFilter; label: string }> = [
    { id: 'todo', label: 'Todo' },
    { id: 'nota', label: 'Notas' },
    { id: 'llamada', label: 'Llamadas' },
    { id: 'correo', label: 'Correos' },
    { id: 'reunion', label: 'Reuniones' },
]

export const noteMeta = (tipo?: string | null): NoteMeta => {
    const known = NOTE_TYPES.find((item) => item.id === tipo)
    if (known) return known
    if (tipo === 'llamada entrante') return { label: 'Llamada', filter: 'llamada', Icon: PhoneCallbackOutlinedIcon, tone: 'bg-tone-teal text-tone-teal-ink' }
    if (tipo === 'adjunto') return { label: 'Adjunto', filter: 'otro', Icon: AttachFileOutlinedIcon, tone: 'bg-tone-pink text-tone-pink-ink' }
    return { label: labelize(tipo) || 'Registro', filter: 'otro', Icon: ForumOutlinedIcon, tone: 'bg-tone-pink text-tone-pink-ink' }
}

export const chipClass = (on: boolean) => `inline-flex min-h-[34px] items-center gap-1.5 rounded-full border px-3 text-[13px] ${on
    ? 'border-k-ink bg-k-ink font-bold text-k-surface'
    : 'border-k-line-strong bg-k-surface font-semibold text-k-ink-2'}`

/** Número para `wa.me`: agrega lada 52 a celulares de 10 dígitos. */
export const phoneHref = (numero?: string | null) => {
    const digits = String(numero || '').replace(/\D/g, '')
    if (!digits) return null
    return digits.length === 10 ? `52${digits}` : digits
}

export const formatCount = (value?: number | string | null) => Number(value || 0).toLocaleString('es-MX')

export const AVATAR_TONES = [
    'bg-tone-blue text-tone-blue-ink',
    'bg-tone-teal text-tone-teal-ink',
    'bg-tone-purple text-tone-purple-ink',
    'bg-tone-orange text-tone-orange-ink',
    'bg-tone-pink text-tone-pink-ink',
]

export const initials = (name?: string | null) => {
    const letters = String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0])
    return letters.join('').toUpperCase() || '—'
}

export const labelize = (value?: string | null) => {
    const text = String(value || '').trim()
    if (!text) return null
    return text.charAt(0).toUpperCase() + text.slice(1)
}

export const money = (value?: number | string | null, code: unknown = 'MXN', fractionDigits = 2) => {
    const amount = Number(value || 0)
    const raw = String(code || '').toUpperCase()
    const currency = /^[A-Z]{3}$/.test(raw) ? raw : 'MXN'
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: fractionDigits }).format(amount)
}

export const errorMessage = (error: unknown, fallback: string) => (error instanceof Error && error.message) || fallback

export const statusKey = (status?: string | null) => String(status || '').toLowerCase()

export const firstFilled = (values: Array<string | null | undefined>) =>
    values.find((value) => String(value || '').trim()) || ''

export const pageWindow = (current: number, total?: number | null) => {
    const last = Math.max(total || 1, 1)
    if (last <= 5) return Array.from({ length: last }, (_, index) => index + 1)
    if (current <= 3) return [1, 2, 3, 4, 5]
    if (current >= last - 2) return [last - 4, last - 3, last - 2, last - 1, last]
    return [current - 2, current - 1, current, current + 1, current + 2]
}

export const ramoName = (ramo: RamoRef) => (typeof ramo === 'string' ? ramo : ramo?.ramo)

const ramoVisual = (ramo: RamoRef): RamoVisual => {
    const value = String(ramoName(ramo) || '').toLowerCase()
    return RAMOS.find((item) => item.test.test(value)) || FALLBACK_RAMO
}

export const RamoMark = ({
    ramo,
    boxed = false,
    size,
    large = false,
}: {
    ramo?: RamoRef
    boxed?: boolean
    size?: number
    large?: boolean
}) => {
    const { Icon, tint, ink } = ramoVisual(ramo)
    const iconSize = size || (large ? 26 : boxed ? 18 : 16)
    const icon = createElement(Icon, { style: { fontSize: iconSize } satisfies CSSProperties })
    if (!boxed) return createElement('span', { className: `inline-flex ${ink}` }, icon)
    const box = large ? 'h-14 w-14 rounded-[18px]' : 'h-9 w-9 rounded-[11px]'
    return createElement('span', { className: `flex shrink-0 items-center justify-center ${box} ${tint}` }, icon)
}
